import React, { useState } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Package, ShoppingCart, Users, Settings, LogOut, Menu, X, Store, Mail } from "lucide-react";

const links = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/messages", label: "Messages", icon: Mail },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isActive = (l) => (l.end ? location.pathname === l.to : location.pathname.startsWith(l.to));

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-white/10">
        <img src="/logo.png" alt="ChainSmart" className="h-11 rounded-sm" />
        <p className="text-xs text-white/40 mt-2 tracking-wider uppercase">Admin Panel</p>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {links.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            onClick={() => setOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors rounded-sm ${isActive(l) ? "bg-gold text-obsidian" : "text-white/70 hover:bg-white/10 hover:text-white"}`}
            data-testid={`admin-nav-${l.label.toLowerCase()}`}
          >
            <l.icon className="w-4 h-4" /> {l.label}
          </Link>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-1">
        <Link to="/" className="flex items-center gap-3 px-3 py-2.5 text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors rounded-sm">
          <Store className="w-4 h-4" /> View Store
        </Link>
        <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors rounded-sm" data-testid="admin-logout">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-secondary flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-60 bg-obsidian fixed inset-y-0 left-0 z-40">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-obsidian"><SidebarContent /></div>
          <div className="flex-1 bg-black/50" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-60 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 bg-obsidian text-white flex items-center justify-between px-4 h-14">
          <button onClick={() => setOpen(true)} data-testid="admin-mobile-menu"><Menu className="w-6 h-6" /></button>
          <img src="/logo.png" alt="" className="h-8 rounded-sm" />
          <span className="text-xs text-white/50">{user?.name}</span>
        </header>
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
